import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { Users, Plus } from "lucide-react";

export default function GroupsPage() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "Community", privacy: "public" });
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get(`/groups`);
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setErr("");
    try {
      const { data } = await api.post(`/groups`, form);
      setCreating(false); setForm({ name:"",description:"",category:"Community",privacy:"public" });
      toast.success("Group created");
      await load();
      if (data?.id) window.location.assign(`/groups/${data.id}`);
    }
    catch (e) { setErr(formatApiError(e)); }
  };

  const toggle = async (g) => {
    try {
      if (g.is_member) { await api.post(`/groups/${g.id}/leave`); toast("Left group"); }
      else { await api.post(`/groups/${g.id}/join`); toast.success(g.privacy === "private" ? "Request sent" : "Joined!"); }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell>
      <div className="space-y-5" data-testid="groups-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// 06 / Groups</div>
            <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Groups</h1>
          </div>
          <button onClick={() => setCreating(true)} className="csc-btn-primary" data-testid="create-group-btn"><Plus size={13} /> New Group</button>
        </div>

        {creating && (
          <div className="csc-card p-5 space-y-3" data-testid="create-group-form">
            <input value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} placeholder="Group name" className="csc-input" data-testid="group-name-input" />
            <textarea value={form.description} onChange={(e) => setForm({...form, description:e.target.value})} placeholder="Description" rows={3} className="csc-input resize-none" data-testid="group-desc-input" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category} onChange={(e) => setForm({...form, category:e.target.value})} className="csc-input" data-testid="group-cat-input">
                {["Cybersecurity","Nonprofit","Education","Community","Other"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <select value={form.privacy} onChange={(e) => setForm({...form, privacy:e.target.value})} className="csc-input" data-testid="group-privacy-input">
                <option value="public">Public</option><option value="private">Private</option>
              </select>
            </div>
            {err && <div className="text-xs text-red-400">{err}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="csc-btn-ghost">Cancel</button>
              <button onClick={create} className="csc-btn-primary" data-testid="group-create-submit">Create</button>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="csc-card p-8 text-center text-[#94a3b8]" data-testid="groups-empty">No groups yet. Start the first one!</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((g) => (
              <div key={g.id} className="csc-card p-5 hover:border-[#00aaff]/50 transition-colors" data-testid={`group-${g.id}`}>
                <Link to={`/groups/${g.id}`} className="block" data-testid={`group-link-${g.id}`}>
                  <div className="flex items-center gap-2"><Users size={14} className="text-[#00aaff]" /><span className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]">{g.category} · {g.privacy}</span></div>
                  <h3 className="text-base font-semibold mt-2">{g.name}</h3>
                  <p className="text-xs text-[#94a3b8] mt-1 line-clamp-3">{g.description}</p>
                </Link>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono-tech text-[10px] text-[#94a3b8]">{g.members} members</span>
                  <button onClick={() => toggle(g)} className={g.is_member ? "csc-btn-ghost" : "csc-btn-secondary"} data-testid={`group-toggle-${g.id}`}>
                    {g.is_member ? "Leave" : g.privacy === "private" ? "Request" : "Join"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
